/**
 * Utility functions for working with pages.
 *
 * These are kept separate from the page utils module because they are used by selectors
 * and we want to avoid circular dependencies.
 */

import {
  ActivityCategory,
  InteractionMode,
  type PageAccessDataType,
  type PageCategory,
  type PageType,
  type SectionPageCategory,
  coursePageCategories,
} from "components/server-types";

export type GroupInteractionModeType =
  | InteractionMode.ASSIGN_STUDENTS
  | InteractionMode.SELF_ASSIGN;

export const GROUP_INTERACTION_MODES = [
  InteractionMode.ASSIGN_STUDENTS,
  InteractionMode.SELF_ASSIGN,
];

export const isSharedClassInteractionMode = (interactionMode: InteractionMode | undefined | null) =>
  interactionMode === InteractionMode.SHARED_CLASS;

export const isSharedClassPage = (page?: Pick<PageType, "interaction_mode"> | null) =>
  isSharedClassInteractionMode(page?.interaction_mode);

export const isGroupInteractionMode = (interactionMode: InteractionMode | undefined) =>
  !!interactionMode && GROUP_INTERACTION_MODES.includes(interactionMode);

export const isGroupPage = (page: Pick<PageType, "interaction_mode"> | null) =>
  isGroupInteractionMode(page?.interaction_mode);

export const isIndividualInteractionMode = (interactionMode: InteractionMode | undefined) =>
  interactionMode === InteractionMode.INDIVIDUAL;

export const isIndividualPage = (page: Pick<PageType, "interaction_mode"> | null) =>
  isIndividualInteractionMode(page?.interaction_mode);

export const isStaticPage = (page: Pick<PageType, "interaction_mode"> | null) =>
  page?.interaction_mode === InteractionMode.STATIC;

export const isSectionPageCategory = (pageCategory: PageCategory) =>
  coursePageCategories.includes(pageCategory as SectionPageCategory);

export const isSectionPage = (page: PageType) => isSectionPageCategory(page.category);

export const isUnassessableCategory = (category: PageCategory) =>
  category === "course_resource" || category === "instructor_workspace";

export const isUnassessablePage = (
  page: PageType | null | undefined,
  activityCategory?: ActivityCategory,
) =>
  Boolean(
    page &&
      (isUnassessableCategory(page.category) ||
        (activityCategory !== undefined && activityCategory === ActivityCategory.ARCHIVE)),
  );

export const isPageDisabled = (
  page: Pick<PageType, "has_linked_submissions"> | null | undefined,
): boolean => Boolean(page?.has_linked_submissions);

/** Keep only page access records that match the page's interaction mode. */
export const filterPageAccessDataByInteractionMode = (
  page: PageType | null | undefined,
  pageAccessData: PageAccessDataType[],
): PageAccessDataType[] => {
  if (!page) {
    return [];
  }

  if (isSharedClassPage(page)) {
    return [];
  }

  if (isGroupPage(page)) {
    return pageAccessData.filter((data) => data.page_group_id != null);
  }

  if (isIndividualPage(page) || isStaticPage(page)) {
    return pageAccessData.filter((data) => data.section_user_id != null);
  }

  return pageAccessData;
};
